import { useMemo, useState } from "react";
import type { VolunteerHistoryItem } from "../types/volunteer";
import { zonedDayKey } from "../lib/timezone";

interface Props {
  history: VolunteerHistoryItem[];
  // Parish IANA timezone — event dots land on the parish-local
  // calendar day, not the viewer's.
  timeZone: string;
}

// Per-day visual state. Priority when multiple events land on the
// same day:
//   checked_in  > no_show > subbed
// "checked_in" wins because if Colleen sees the green dot she knows
// they showed up; the underlying sub or scheduled-no-show context is
// secondary. The events array carries the raw items for any future
// hover/popover.
type DayState = "empty" | "checked_in" | "no_show" | "subbed";

interface DayBucket {
  state: DayState;
  events: VolunteerHistoryItem[];
}

export default function VolunteerCalendar({ history, timeZone }: Props) {
  const buckets = useMemo(() => bucketByDay(history, timeZone), [history, timeZone]);
  const totals = useMemo(() => countByState(buckets), [buckets]);
  const [month, setMonth] = useState<Date>(() => pickDefaultMonth(history, timeZone));
  const grid = useMemo(() => buildMonthGrid(month), [month]);

  const earliest = useMemo(() => earliestEventMonth(history, timeZone), [history, timeZone]);
  const today = useMemo(() => firstOfMonth(new Date()), []);
  const canPrev = earliest ? sameOrAfter(month, addMonths(earliest, 1)) : false;
  const canNext = sameOrBefore(month, addMonths(today, -1));

  return (
    <section className="volunteer-cal" aria-label="Service calendar">
      <header className="volunteer-cal__header">
        <button
          type="button"
          className="volunteer-cal__nav"
          onClick={() => setMonth((m) => addMonths(m, -1))}
          disabled={!canPrev}
          aria-label="Previous month"
        >
          ‹
        </button>
        <span className="volunteer-cal__month">{monthLabel(month)}</span>
        <button
          type="button"
          className="volunteer-cal__nav"
          onClick={() => setMonth((m) => addMonths(m, 1))}
          disabled={!canNext}
          aria-label="Next month"
        >
          ›
        </button>
      </header>

      <div className="volunteer-cal__weekdays" aria-hidden="true">
        {WEEKDAY_LABELS.map((d) => (
          <span key={d} className="volunteer-cal__weekday">{d}</span>
        ))}
      </div>

      <ol className="volunteer-cal__grid">
        {grid.map((cell, i) => {
          const key = dayKey(cell.date);
          const bucket = buckets.get(key);
          const state = cell.inMonth ? bucket?.state ?? "empty" : "empty";
          const className = [
            "volunteer-cal__day",
            !cell.inMonth ? "volunteer-cal__day--out" : "",
            state !== "empty" ? `volunteer-cal__day--${state}` : "",
            isSameDay(cell.date, new Date()) ? "volunteer-cal__day--today" : "",
          ]
            .filter(Boolean)
            .join(" ");
          return (
            <li key={i} className={className} aria-label={cellAriaLabel(cell, bucket)}>
              <span className="volunteer-cal__marker">{cell.date.getDate()}</span>
            </li>
          );
        })}
      </ol>

      <ul className="volunteer-cal__legend" aria-label="Calendar legend">
        <li>
          <span className="volunteer-cal__dot volunteer-cal__dot--checked_in" aria-hidden="true" />
          Checked in <span className="volunteer-cal__legend-count">({totals.checked_in})</span>
        </li>
        <li>
          <span className="volunteer-cal__dot volunteer-cal__dot--no_show" aria-hidden="true" />
          Scheduled, no check-in <span className="volunteer-cal__legend-count">({totals.no_show})</span>
        </li>
        <li>
          <span className="volunteer-cal__dot volunteer-cal__dot--subbed" aria-hidden="true" />
          Subbed <span className="volunteer-cal__legend-count">({totals.subbed})</span>
        </li>
      </ul>
    </section>
  );
}

// ---------- helpers ----------

const WEEKDAY_LABELS = ["S", "M", "T", "W", "T", "F", "S"];

function countByState(buckets: Map<string, DayBucket>): Record<DayState, number> {
  const counts: Record<DayState, number> = {
    empty: 0,
    checked_in: 0,
    no_show: 0,
    subbed: 0,
  };
  for (const b of buckets.values()) counts[b.state]++;
  return counts;
}

function bucketByDay(
  history: VolunteerHistoryItem[],
  timeZone: string,
): Map<string, DayBucket> {
  const map = new Map<string, DayBucket>();
  for (const item of history) {
    // Calendar dots key off the Mass day, not the action day. For
    // check-ins and assignments those are the same; for sub_opened /
    // sub_claimed the Mass day is the one that matters. The day is
    // the PARISH's calendar day — grid cells key by plain calendar
    // components, so the two align by construction.
    const iso = item.massDate ?? item.date;
    const key = zonedDayKey(new Date(iso), timeZone);
    const existing = map.get(key) ?? { state: "empty", events: [] };
    existing.events.push(item);
    existing.state = mergeState(existing.state, classify(item));
    map.set(key, existing);
  }
  return map;
}

function classify(item: VolunteerHistoryItem): DayState {
  if (item.kind === "checkin") return "checked_in";
  if (item.kind === "sub_opened" || item.kind === "sub_claimed") return "subbed";
  // assignment — we know the Mass has passed (worker filters future
  // assignments out). If a check-in also exists for the same day the
  // merge will upgrade to checked_in. If we only ever see assignment,
  // the volunteer was scheduled and didn't tap in: a no-show.
  return "no_show";
}

// Priority: checked_in > no_show > subbed.
// Reads: if they showed up, that's the headline. If they were
// scheduled and didn't show, surface that. Otherwise a sub-day is
// the third tier.
function mergeState(prev: DayState, next: DayState): DayState {
  const order: DayState[] = ["empty", "subbed", "no_show", "checked_in"];
  return order[Math.max(order.indexOf(prev), order.indexOf(next))]!;
}

// Month selection derives from the same parish-local day keys the
// dots bucket by — deriving it from viewer-local Date components can
// disagree with the buckets by a month near day boundaries (e.g. a
// parish-local Jul 31 evening Mass is Aug 1 UTC), opening the
// calendar on an empty grid. Day keys sort lexicographically.
function eventDayKeys(history: VolunteerHistoryItem[], timeZone: string): string[] {
  return history
    .map((h) => zonedDayKey(new Date(h.massDate ?? h.date), timeZone))
    .filter((k) => /^\d{4}-/.test(k));
}

function monthFromKey(key: string): Date {
  return new Date(Number(key.slice(0, 4)), Number(key.slice(5, 7)) - 1, 1);
}

function pickDefaultMonth(history: VolunteerHistoryItem[], timeZone: string): Date {
  // Most recent event's month, or current month if no history.
  const keys = eventDayKeys(history, timeZone);
  if (keys.length === 0) return firstOfMonth(new Date());
  return monthFromKey(keys.reduce((a, b) => (b > a ? b : a)));
}

function earliestEventMonth(history: VolunteerHistoryItem[], timeZone: string): Date | null {
  const keys = eventDayKeys(history, timeZone);
  if (keys.length === 0) return null;
  return monthFromKey(keys.reduce((a, b) => (b < a ? b : a)));
}

interface GridCell {
  date: Date;
  inMonth: boolean;
}

function buildMonthGrid(month: Date): GridCell[] {
  const first = firstOfMonth(month);
  const startWeekday = first.getDay(); // 0=Sun ... 6=Sat
  const start = addDays(first, -startWeekday);
  // 6 weeks * 7 days = 42 cells; covers every possible month layout.
  return Array.from({ length: 42 }, (_, i) => {
    const date = addDays(start, i);
    return { date, inMonth: date.getMonth() === month.getMonth() };
  });
}

function cellAriaLabel(cell: GridCell, bucket: DayBucket | undefined): string {
  const dateStr = cell.date.toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
  });
  if (!bucket || bucket.state === "empty") return dateStr;
  const stateText = {
    checked_in: "checked in",
    no_show: "scheduled but did not check in",
    subbed: "sub-request",
    empty: "",
  }[bucket.state];
  return `${dateStr}, ${stateText}`;
}

// ---------- date math ----------

function firstOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
function addMonths(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth() + n, 1);
}
function addDays(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
}
function dayKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}
function sameOrAfter(a: Date, b: Date): boolean {
  return a.getTime() >= b.getTime();
}
function sameOrBefore(a: Date, b: Date): boolean {
  return a.getTime() <= b.getTime();
}
function monthLabel(d: Date): string {
  return d.toLocaleString("en-US", { month: "long", year: "numeric" });
}
